import { ApolloError } from "@apollo/client";
import { Feature, Point, Position } from "geojson";
import { useState } from "react";

import { GqlPlannerTravelGroupOwnerBillingType } from "~/__generated__";
import { GaiaMapFeatureDragListener } from "~/modules/map/components/GaiaMapEventListeners/GaiaMapFeatureDragListener/";
import { GaiaMapMarkerFieldworkerDraggable } from "~/modules/plannerV2/components/GaiaMapMarkerFieldworkerIcon/GaiaMapMarkerFieldworkerDraggable";
import { GaiaMapMarkerFieldworkerDroppable } from "~/modules/plannerV2/components/GaiaMapMarkerFieldworkerIcon/GaiaMapMarkerFieldworkerDroppable";
import { PickupPointLayer, PickupPointProperties } from "~/modules/plannerV2/components/map-layers/PickupPointLayer";

import { PickupPointLayerFragment } from "../PickupPointLayer/fragments.generated";
import { PickupActionError } from "./PickupActionError";
import { PickupActionLoadingGhost } from "./PickupActionGhost";

interface DraggablePickupPointLayerProps {
    points: PickupPointLayerFragment[];
    ownerTravelMethod: GqlPlannerTravelGroupOwnerBillingType;
    onPickupDrag?: (
        feature: Feature<Point, PickupPointProperties>,
        newLocation: [number, number],
        droppedOnFeature?: Feature<Point, PickupPointProperties>,
    ) => Promise<void>;
    pendingPickupAction: {
        location: Position;
        type: "move" | "merge";
        loading: boolean;
        error?: ApolloError;
    } | null;
}

const LAYER_ID_PICKUP_POINTS = "pickup_points";

export function DraggablePickupPointLayer({
    points,
    ownerTravelMethod,
    onPickupDrag,
    pendingPickupAction,
}: DraggablePickupPointLayerProps) {
    const [draggedFieldworkerId, setDraggedFieldworkerId] = useState<string | undefined>(undefined);

    return (
        <>
            <PickupPointLayer
                ownerTravelMethod={ownerTravelMethod}
                excludeId={draggedFieldworkerId}
                layerId={LAYER_ID_PICKUP_POINTS}
                points={points}
            />

            <GaiaMapFeatureDragListener<Feature<Point, PickupPointProperties>>
                layerIds={[`${LAYER_ID_PICKUP_POINTS}_icon`]}
                isDraggable={worker => worker?.properties?.isDraggable}
                isDropTarget={(worker1, worker2) => {
                    return worker1.properties?.id !== worker2.properties?.id;
                }}
                callbacks={{
                    onDragStart: feature => {
                        setDraggedFieldworkerId(feature?.properties?._id);
                    },
                    onDragEnd: async (feature, geoPosition, droppedOnFeature) => {
                        try {
                            await onPickupDrag?.(feature, geoPosition, droppedOnFeature);
                        } finally {
                            setDraggedFieldworkerId(undefined);
                        }
                    },
                }}
            >
                {({ isDragging, currentGeoPosition, hoveredFeature }) =>
                    isDragging &&
                    currentGeoPosition && (
                        <>
                            <GaiaMapMarkerFieldworkerDraggable coordinates={currentGeoPosition} />
                            {hoveredFeature && (
                                <GaiaMapMarkerFieldworkerDroppable coordinates={hoveredFeature.geometry.coordinates} />
                            )}
                        </>
                    )
                }
            </GaiaMapFeatureDragListener>

            {pendingPickupAction?.loading === true && (
                <PickupActionLoadingGhost
                    coordinates={pendingPickupAction.location}
                    actionType={pendingPickupAction.type}
                />
            )}

            {pendingPickupAction?.loading === false && pendingPickupAction?.error && (
                <PickupActionError coordinates={pendingPickupAction.location} error={pendingPickupAction.error} />
            )}
        </>
    );
}
